import { useMutation } from '@tanstack/react-query';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import type { ToastData } from '~/hooks/useToast';
import type { CommunityScheduledPost } from '~/interfaces/community';

export type UpdateCommunityScheduledPostResponse =
  | { scheduled_post: CommunityScheduledPost; toast?: ToastData }
  | { toast: ToastData };

const isUpdateCommunityScheduledPostResponse = (data: unknown): data is UpdateCommunityScheduledPostResponse =>
  typeof data === 'object' && data !== null && ('scheduled_post' in data || 'toast' in data);

// Returns a refusal as data, like the create, so the composer shows it inline.
const useUpdateCommunityScheduledPostMutation = () => {
  const ensureCsrfToken = useEnsureCsrfToken();
  return useMutation({
    mutationFn: async (payload: Record<string, string>): Promise<UpdateCommunityScheduledPostResponse> => {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch('/update_community_scheduled_post', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          'X-CSRF-Token': csrfToken,
        },
        body: new URLSearchParams(payload),
      });

      const data: unknown = await response.json();
      if (!isUpdateCommunityScheduledPostResponse(data)) throw new Error('composer_invalid_response');

      return data;
    },
  });
};

export default useUpdateCommunityScheduledPostMutation;
